import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Download, Mail, Phone, MapPin, Send, Loader2, Linkedin } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-pine border border-treeline text-cream font-mono text-sm px-4 py-3.5 placeholder:text-sage/50 focus:outline-none focus:border-lime transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. Sarron will get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send your message. Please email me directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-28 md:py-40 border-t border-treeline bg-pine/40">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-lime mb-6"
        >
          [ 05 ] · Contact
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream mb-20"
        >
          Let's <span className="italic text-lime">Connect.</span>
        </motion.h2>

        <div className="grid grid-cols-12 gap-12 md:gap-20">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="col-span-12 md:col-span-6 space-y-5"
            data-testid="contact-form"
          >
            <div>
              <label htmlFor="contact-name" className="block font-mono text-xs uppercase tracking-[0.25em] text-sage mb-2">Name</label>
              <input
                id="contact-name"
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block font-mono text-xs uppercase tracking-[0.25em] text-sage mb-2">Email</label>
              <input
                id="contact-email"
                data-testid="contact-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@company.com"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="block font-mono text-xs uppercase tracking-[0.25em] text-sage mb-2">Message</label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell me about the role or project…"
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] bg-lime text-forest px-8 py-4 hover:bg-limedeep disabled:opacity-60 transition-colors duration-300"
            >
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              {sending ? "Sending…" : "Send message"}
            </button>
          </motion.form>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
            className="col-span-12 md:col-span-6 md:pl-10 md:border-l md:border-treeline flex flex-col gap-10"
          >
            <div className="space-y-6">
              <a
                data-testid="contact-email-link"
                href="mailto:sarrontadesse@gmail.com"
                className="flex items-center gap-4 group w-fit"
              >
                <Mail size={18} className="text-lime shrink-0" />
                <span className="font-mono text-sm md:text-base text-cream group-hover:text-lime transition-colors duration-300">
                  sarrontadesse@gmail.com
                </span>
              </a>
              <a data-testid="contact-phone-link" href="tel:+18723029404" className="flex items-center gap-4 group w-fit">
                <Phone size={18} className="text-lime shrink-0" />
                <span className="font-mono text-sm md:text-base text-cream group-hover:text-lime transition-colors duration-300">
                  +1 872-302-9404
                </span>
              </a>
              <div className="flex items-center gap-4" data-testid="contact-location">
                <MapPin size={18} className="text-lime shrink-0" />
                <span className="font-mono text-sm md:text-base text-sage">San Francisco, CA</span>
              </div>
              <a
                data-testid="contact-linkedin-link"
                href="https://www.linkedin.com/in/sarron-tadesse-114aab1b9"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group w-fit"
              >
                <Linkedin size={18} className="text-lime shrink-0" />
                <span className="font-mono text-sm md:text-base text-cream group-hover:text-lime transition-colors duration-300">
                  linkedin.com/in/sarron-tadesse
                </span>
              </a>
            </div>

            <a
              data-testid="download-resume-button"
              href="/assets/Sarron_Tadesse_Resume.pdf"
              download
              className="group relative inline-flex items-center justify-between gap-6 border border-lime p-6 md:p-8 overflow-hidden w-full"
            >
              <div className="absolute inset-0 bg-lime translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              <div className="relative z-10">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lime group-hover:text-forest/70 transition-colors duration-300">
                  Full resume
                </p>
                <p className="mt-2 font-display text-2xl md:text-3xl text-cream group-hover:text-forest transition-colors duration-300">
                  Download PDF
                </p>
              </div>
              <Download
                size={28}
                className="relative z-10 text-lime group-hover:text-forest group-hover:translate-y-1 transition-all duration-300"
              />
            </a>

            <div className="flex flex-wrap gap-4">
              <a
                data-testid="contact-linkedin-pill"
                href="https://www.linkedin.com/in/sarron-tadesse-114aab1b9"
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs uppercase tracking-[0.25em] border border-treeline text-sage px-5 py-3 hover:border-lime hover:text-lime transition-colors duration-300"
              >
                LinkedIn
              </a>
              <a
                data-testid="contact-github-link"
                href="https://github.com/Sarronnn"
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs uppercase tracking-[0.25em] border border-treeline text-sage px-5 py-3 hover:border-lime hover:text-lime transition-colors duration-300"
              >
                GitHub
              </a>
              <a
                data-testid="contact-colab-link"
                href="https://colab.research.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs uppercase tracking-[0.25em] border border-treeline text-sage px-5 py-3 hover:border-lime hover:text-lime transition-colors duration-300"
              >
                Colab
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
